import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import TermsAndPrivacyModal from '../components/TermsAndPrivacyModal';
import '../styles/Login.css';
import '../styles/TermsAndPrivacyModal.css';

// Xalqaro xavfsizlik standarti: Parol mustahkamligini hisoblash
const calculatePasswordStrength = (pass) => {
  if (!pass) {
    return {
      score: 0,
      percent: 0,
      label: '',
      color: '#cbd5e1',
      checks: { length: false, cases: false, numOrSymbol: false }
    };
  }

  const hasLength = pass.length >= 8;
  const hasCases = /[a-z]/.test(pass) && /[A-Z]/.test(pass);
  const hasNumOrSymbol = /[0-9]/.test(pass) || /[^A-Za-z0-9]/.test(pass);

  let score = 0;
  if (pass.length >= 6) score += 1;
  if (hasLength) score += 1;
  if (hasCases) score += 1;
  if (hasNumOrSymbol) score += 1;

  let label = 'Zaif';
  let color = '#ef4444';
  let percent = 25;

  if (score === 2) {
    label = 'Õrtaça';
    color = '#f59e0b';
    percent = 50;
  } else if (score === 3) {
    label = 'Yaxşi';
    color = '#0284c7';
    percent = 75;
  } else if (score >= 4) {
    label = 'Kuçli (Tavsiya etiladi)';
    color = '#10b981';
    percent = 100;
  }

  return {
    score,
    percent,
    label,
    color,
    checks: {
      length: hasLength,
      cases: hasCases,
      numOrSymbol: hasNumOrSymbol
    }
  };
};

export default function RegisterAccount({ setUser, setToken }) {
  const navigate = useNavigate();

  // Registration form state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedNotice, setGeneratedNotice] = useState('');

  // Step state: 'form' | 'verify'
  const [step, setStep] = useState('form');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [infoSuccess, setInfoSuccess] = useState('');

  // Terms and Privacy modal state
  const [showTermsModal, setShowTermsModal] = useState(false);

  const strength = calculatePasswordStrength(password);

  // Dunyo tajribasidan kelib chiqib: 1-bosqichda avtomatik kuçli va esda qolarli parol generatsiya qilish
  const handleGenerateStrongPassword = (e) => {
    e.preventDefault();
    const uppers = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lowers = 'abcdefghijkmnopqrstuvwxyz';
    const digits = '23456789';
    const symbols = '!@#$%&*';

    let pass = '';
    pass += uppers[Math.floor(Math.random() * uppers.length)];
    pass += uppers[Math.floor(Math.random() * uppers.length)];
    pass += lowers[Math.floor(Math.random() * lowers.length)];
    pass += lowers[Math.floor(Math.random() * lowers.length)];
    pass += lowers[Math.floor(Math.random() * lowers.length)];
    pass += lowers[Math.floor(Math.random() * lowers.length)];
    pass += digits[Math.floor(Math.random() * digits.length)];
    pass += digits[Math.floor(Math.random() * digits.length)];
    pass += symbols[Math.floor(Math.random() * symbols.length)];
    pass += symbols[Math.floor(Math.random() * symbols.length)];

    // Shuffle characters
    pass = pass.split('').sort(() => 0.5 - Math.random()).join('');

    setPassword(pass);
    setConfirmPassword(pass);
    setShowPassword(true);
    setShowConfirmPassword(true);
    setGeneratedNotice('✓ Tavsiya etilgan xavfsiz parol yaratildi va ikkala maydonga kiritildi!');
    setTimeout(() => setGeneratedNotice(''), 5000);
  };

  // Timer countdown for resending verification email
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();

    if (!fullName.trim()) {
      setError('Iltimos, tõliq familiya, ism va sarifingizni kiriting');
      return;
    }
    if (!email.trim()) {
      setError('Iltimos, elektron poçta manzilingizni kiriting');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Elektron poçta manzili formati nõtõğri (masalan: talaba@turon.uz yoki ismingiz@gmail.com)');
      return;
    }
    if (!password) {
      setError('Iltimos, parol õylab toping yoki «⚡ Kuçli parol yaratış» tugmasidan foydalaning');
      return;
    }
    if (password.length < 8) {
      setError('Xavfsizlik talablariga muvofiq, parol kamida 8 ta belgidan iborat bõlişi lozim');
      return;
    }
    if (password !== confirmPassword) {
      setError('Kiritilgan parollar bir-biriga mos kelmadi');
      return;
    }
    if (!agreeTerms) {
      setError('Foydalaniş şartlari va maxfiylik siyosatiga rozilik berişingiz lozim');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setInfoSuccess('');

      const response = await axios.post('/api/auth/register-account', {
        fullName: fullName.trim(),
        email: email.trim().toLowerCase(),
        password: password
      });

      if (response.data.requireVerification) {
        setStep('verify');
        setInfoSuccess(response.data.message || 'Tasdiqlaş xabari emailingizga yuborildi!');
        setResendCooldown(60);
      } else if (response.data.token) {
        const { token, user } = response.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        if (setToken) setToken(token);
        if (setUser) setUser(user);
        navigate('/');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.error || 'Rõyxatdan õtişda xatolik yuz berdi. Maʼlumotlarni tekşiring.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCodeSubmit = async (e, codeOverride) => {
    if (e) e.preventDefault();
    const codeToVerify = codeOverride || verificationCode;
    if (!codeToVerify.trim()) {
      setError('Iltimos, poçtangizga yuborilgan 6 xonali tasdiqlaş kodini kiriting');
      return;
    }

    try {
      setVerifyLoading(true);
      setError('');

      const res = await axios.post('/api/auth/verify-email', {
        email: email.trim().toLowerCase(),
        code: codeToVerify.trim()
      });

      const { token, user } = res.data;
      if (token && user) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        if (setToken) setToken(token);
        if (setUser) setUser(user);
      }

      setInfoSuccess('✓ Hisobingiz muvaffaqiyatli faollaştirildi! Tizimga yõnaltirilmoqdasiz...');
      setTimeout(() => {
        navigate('/');
      }, 1400);
    } catch (err) {
      setError(err.response?.data?.error || 'Kiritilgan tasdiqlaş kodi nõtõğri yoki muddati eskirgan');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || resending) return;
    try {
      setResending(true);
      setError('');
      const res = await axios.post('/api/auth/resend-verification', {
        email: email.trim().toLowerCase()
      });
      setInfoSuccess(res.data.message || 'Yangi tasdiqlaş kodi poçtangizga yuborildi!');
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.error || 'Xat yuborişda xatolik yuz berdi');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="login-page-wrapper">
      <div className="login-card-container">
        {/* Turon universiteti rasmiy logotipi */}
        <div className="university-brand-header" onClick={() => navigate('/')} title="Boş sahifaga õtiş">
          <img src="/logo.png" alt="Turon University" className="turon-auth-logo" />
        </div>

        {/* 1-BOSQICH: Rõyxatdan õtiş formasi */}
        {step === 'form' && (
          <div className="auth-box-card">
            <h2 className="auth-card-title">Yangi hisob yarating</h2>

            {error && (
              <div className="auth-alert-error">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleRegisterSubmit} className="auth-login-form">
              {/* F.I.SH */}
              <div className="input-field-group">
                <label htmlFor="reg-fullname">F.I.Ş</label>
                <input
                  id="reg-fullname"
                  type="text"
                  placeholder="Familiya ism sarifingiz"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={loading}
                  autoFocus
                />
              </div>

              {/* Email */}
              <div className="input-field-group">
                <label htmlFor="reg-email">Elektron poçta (Email)</label>
                <input
                  id="reg-email"
                  type="email"
                  placeholder="masalan: talaba@turon.uz yoki gmail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* Parol õylab topiş + Kuçli parol generatsiya qilish tugmasi */}
              <div className="input-field-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <label htmlFor="reg-password" style={{ margin: 0 }}>Parol õylab toping</label>
                  <button
                    type="button"
                    className="pass-generator-btn"
                    onClick={handleGenerateStrongPassword}
                    title="Tizim tomonidan xavfsiz va kuçli parol avtomatik shakllantiriladi"
                  >
                    <span>⚡</span>
                    <span>Kuçli parol yaratış</span>
                  </button>
                </div>

                <div className="password-input-wrapper">
                  <input
                    id="reg-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Parol kiriting yoki avtomatik yarating"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="eye-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                    aria-label="Parolni kõrsatiş"
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                    )}
                  </button>
                </div>

                {generatedNotice && (
                  <div className="pass-gen-badge">
                    <span>{generatedNotice}</span>
                  </div>
                )}

                {/* Parol mustahkamlik indikatori (World-standard password meter) */}
                {password && (
                  <div className="pass-strength-container">
                    <div className="strength-bar-track">
                      <div
                        className="strength-bar-fill"
                        style={{ width: `${strength.percent}%`, background: strength.color }}
                      />
                    </div>
                    <div className="strength-meta-row">
                      <span style={{ color: strength.color, fontWeight: '700' }}>
                        Mustahkamligi: {strength.label}
                      </span>
                      <span style={{ color: '#94a3b8' }}>kamida 8 belgi</span>
                    </div>

                    <div className="pass-checklist">
                      <span className={`pass-check-item ${strength.checks.length ? 'met' : ''}`}>
                        {strength.checks.length ? '✓' : '○'} Kamida 8 ta belgi
                      </span>
                      <span className={`pass-check-item ${strength.checks.cases ? 'met' : ''}`}>
                        {strength.checks.cases ? '✓' : '○'} Katta va kiçik harflar (A-z)
                      </span>
                      <span className={`pass-check-item ${strength.checks.numOrSymbol ? 'met' : ''}`}>
                        {strength.checks.numOrSymbol ? '✓' : '○'} Raqam yoki belgi (0-9, @, #...)
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Parolni tasdiqlash */}
              <div className="input-field-group">
                <label htmlFor="reg-confirm-password">Parolni tasdiqlang</label>
                <div className="password-input-wrapper">
                  <input
                    id="reg-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Parolni qayta kiriting"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="eye-toggle-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex="-1"
                    aria-label="Parolni kõrsatiş"
                  >
                    {showConfirmPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                    )}
                  </button>
                </div>
                {confirmPassword && password && confirmPassword === password && (
                  <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: '600', marginTop: '4px', display: 'block' }}>
                    ✓ Parollar bir-biriga mos keldi
                  </span>
                )}
              </div>

              {/* Foydalanish shartlari va Maxfiylik siyosati */}
              <div className="checkbox-remember-row" style={{ alignItems: 'flex-start', margin: '14px 0 20px 0' }}>
                <label className="checkbox-container" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                  />
                  <span className="checkmark-box"></span>
                  <span className="remember-label-text" style={{ fontSize: '13px', lineHeight: '1.45' }}>
                    <span>Foydalaniş şartlari va maxfiylik siyosati bilan tanişdim va roziman </span>
                    <button
                      type="button"
                      className="terms-detail-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowTermsModal(true);
                      }}
                      title="Rasmiy shartlar va maxfiylik siyosatini tõliq õqiş"
                    >
                      (Batafsil)
                    </button>
                  </span>
                </label>
              </div>

              <button type="submit" className="btn-main-submit" disabled={loading}>
                {loading ? 'Yuborilmoqda...' : 'Rõyxatdan õtiş (Kodni oliş) →'}
              </button>
            </form>
          </div>
        )}

        {/* 2-BOSQICH: Email tasdiqlash oynasi (Dunyo tajribasidagi OTP & Magic Link tekshiruvi) */}
        {step === 'verify' && (
          <div className="auth-box-card">
            <div style={{ textAlign: 'center', fontSize: '38px', marginBottom: '6px' }}>✉️</div>
            <h2 className="auth-card-title">Poçtani tasdiqlang</h2>

            {infoSuccess && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', padding: '12px 14px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '14px', lineHeight: '1.5' }}>
                {infoSuccess}
              </div>
            )}

            {error && (
              <div className="auth-alert-error">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                <span>{error}</span>
              </div>
            )}

            <p style={{ fontSize: '13.5px', color: '#475569', lineHeight: '1.55', margin: '0 0 6px 0' }}>
              Xalqaro xavfsizlik standartlariga muvofiq, hisobingizni faollaştiriş ucun 6 xonali tasdiqlaş kodi va faollaştiriş havolasi quyidagi poçtaga yuborildi:
            </p>

            <div className="verify-email-chip">
              <span style={{ fontWeight: '700' }}>{email}</span>
              <button
                type="button"
                className="btn-change-email"
                onClick={() => setStep('form')}
                title="Pochta manzilini õzgartiriş"
              >
                ✏️ Õzgartiriş
              </button>
            </div>

            <form onSubmit={handleVerifyCodeSubmit} className="auth-login-form">
              <div className="input-field-group">
                <label htmlFor="reg-verify-code">6 xonali tasdiqlaş kodini kiriting:</label>
                <input
                  id="reg-verify-code"
                  type="text"
                  maxLength={6}
                  placeholder="123456"
                  value={verificationCode}
                  onChange={(e) => {
                    const clean = e.target.value.replace(/\D/g, '');
                    setVerificationCode(clean);
                    if (clean.length === 6) {
                      handleVerifyCodeSubmit(null, clean);
                    }
                  }}
                  style={{ letterSpacing: '6px', fontSize: '22px', textAlign: 'center', fontWeight: '800' }}
                  autoFocus
                  required
                />
              </div>

              <button type="submit" className="btn-main-submit" disabled={verifyLoading}>
                {verifyLoading ? 'Tekşirilmoqda...' : 'Tasdiqlaş va hisobga kiriş'}
              </button>
            </form>

            <div style={{ marginTop: '20px', textAlign: 'center', borderTop: '1px solid #f1f5f9', paddingTop: '14px', fontSize: '13px' }}>
              <p style={{ margin: '0 0 8px 0', color: '#64748b' }}>
                Emailingizga xat kelmadimi? (Spam papkasini ham tekşiring)
              </p>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendCooldown > 0 || resending}
                style={{
                  background: 'none',
                  border: 'none',
                  color: resendCooldown > 0 ? '#94a3b8' : '#0284c7',
                  fontWeight: '700',
                  cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
                  textDecoration: resendCooldown > 0 ? 'none' : 'underline',
                  padding: 0
                }}
              >
                {resending
                  ? 'Yuborilmoqda...'
                  : resendCooldown > 0
                  ? `Kodni qayta yuboriş (${resendCooldown}s)`
                  : 'Tasdiqlaş xatini qayta yuboriş'}
              </button>
            </div>
          </div>
        )}

        {/* Hisobingiz bormi? Kirish */}
        <div className="auth-footer-prompt">
          <span>Hisobingiz bormi? </span>
          <Link to="/login" className="register-now-link">Kiriş</Link>
        </div>
      </div>

      {/* Foydalanish shartlari va Maxfiylik siyosati modali */}
      <TermsAndPrivacyModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={() => {
          setAgreeTerms(true);
          setShowTermsModal(false);
        }}
      />
    </div>
  );
}
